import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
export const dayKey = (d) => KEYS[(d.getDay() + 6) % 7];
const blankWeek = () => Object.fromEntries(KEYS.map((k) => [k, { available: true, start: null, end: null }]));

const MyAvailability = () => {
  const [weekly, setWeekly] = useState(blankWeek());
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get("/availability").then((r) => { if (r.data[0]) { setWeekly({ ...blankWeek(), ...r.data[0].weekly }); setNote(r.data[0].note || ""); } });
  }, []);
  const set = (k, v) => setWeekly({ ...weekly, [k]: { ...weekly[k], ...v } });
  const save = async () => {
    try { await api.put("/availability", { weekly, note: note || null }); toast.success("Availability saved"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="max-w-2xl space-y-2 rounded-2xl border border-slate-200 bg-white p-5" data-testid="availability-editor">
      <p className="mb-2 text-sm text-slate-500">Let your manager know when you can work. Leave times blank if you're free all day.</p>
      {KEYS.map((k) => (
        <div key={k} className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-2">
          <span className="w-24 text-sm font-medium">{LABELS[k]}</span>
          <Switch checked={weekly[k].available} onCheckedChange={(v) => set(k, { available: v })} data-testid={`avail-toggle-${k}`} />
          <span className="w-20 text-xs text-slate-500">{weekly[k].available ? "Available" : "Unavailable"}</span>
          {weekly[k].available && <>
            <Input type="time" value={weekly[k].start || ""} onChange={(e) => set(k, { start: e.target.value || null })} className="w-28 rounded-xl" data-testid={`avail-start-${k}`} />
            <Input type="time" value={weekly[k].end || ""} onChange={(e) => set(k, { end: e.target.value || null })} className="w-28 rounded-xl" data-testid={`avail-end-${k}`} />
          </>}
        </div>
      ))}
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything else? e.g. uni exams in November" className="mt-3 rounded-xl" data-testid="avail-note" />
      <Button onClick={save} className="mt-2 rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="avail-save-btn">Save availability</Button>
    </div>
  );
};

const TeamAvailability = ({ members }) => {
  const [rows, setRows] = useState({});
  useEffect(() => {
    api.get("/availability").then((r) => setRows(Object.fromEntries(r.data.map((a) => [a.membership_id, a]))));
  }, []);
  const cell = (a) => (!a ? "—" : !a.available ? <span className="text-red-600">No</span> : a.start ? `${a.start}–${a.end || "close"}` : <span className="text-emerald-700">Any</span>);
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="team-availability">
      <table className="w-full min-w-[760px] text-sm"><thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-3 py-2">Employee</th>{KEYS.map((k) => <th key={k} className="px-3 py-2">{LABELS[k].slice(0, 3)}</th>)}<th className="px-3 py-2">Note</th></tr></thead>
        <tbody>{members.map((m) => (
          <tr key={m.membership_id} className="border-t border-slate-100"><td className="px-3 py-2 font-medium">{m.name}</td>
            {KEYS.map((k) => <td key={k} className="px-3 py-2 text-xs">{cell(rows[m.membership_id]?.weekly?.[k])}</td>)}
            <td className="px-3 py-2 text-xs text-slate-500">{rows[m.membership_id]?.note || (rows[m.membership_id] ? "" : "Not set yet")}</td></tr>))}</tbody></table>
    </div>
  );
};

export const AvailabilityPanel = ({ mgr, members }) => (mgr ? <div className="space-y-6"><TeamAvailability members={members} /><div><h3 className="mb-2 font-semibold">My availability</h3><MyAvailability /></div></div> : <MyAvailability />);

export const OfferSwapDialog = ({ shift, onClose, onSaved }) => {
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState("anyone");
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get("/colleagues").then((r) => setPeople(r.data));
  }, []);
  const send = async () => {
    try { await api.post("/swaps", { roster_shift_id: shift.roster_shift_id, to_membership_id: to === "anyone" ? null : to, note: note || null }); toast.success("Swap request sent"); onSaved(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="swap-dialog">
        <DialogHeader><DialogTitle>Swap {shift.date} · {shift.start}–{shift.end}</DialogTitle></DialogHeader>
        <Select value={to} onValueChange={setTo}><SelectTrigger className="rounded-xl" data-testid="swap-to-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="anyone">Offer to anyone</SelectItem>{people.map((p) => <SelectItem key={p.membership_id} value={p.membership_id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" className="rounded-xl" data-testid="swap-note" />
        <p className="text-xs text-slate-500">Your colleague accepts first, then a manager approves before the roster changes.</p>
        <DialogFooter><Button onClick={send} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="swap-send-btn">Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const STATUS = { pending_colleague: ["Waiting for colleague", "bg-amber-50 text-amber-800"], pending_manager: ["Waiting for manager", "bg-sky-50 text-sky-800"], approved: ["Approved", "bg-emerald-50 text-emerald-700"], rejected: ["Rejected", "bg-red-50 text-red-700"], cancelled: ["Cancelled", "bg-slate-100 text-slate-500"] };

export const SwapsPanel = ({ mgr, wps, refreshKey, onChanged }) => {
  const { membership } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/swaps").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  const act = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); load(); onChanged(); } catch (e) { toast.error(formatError(e)); } };
  const me = membership.membership_id;
  if (!items.length) return <p className="rounded-2xl bg-white p-8 text-center text-sm text-slate-500" data-testid="swaps-empty">No shift swaps yet.{!mgr && " Tap Swap on one of your shifts to offer it."}</p>;
  return (
    <ul className="space-y-3" data-testid="swaps-list">{items.map((s) => (
      <li key={s.swap_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`swap-${s.swap_id}`}>
        <div className="text-sm"><p className="font-semibold">{s.shift.date} · {s.shift.start}–{s.shift.end} <span className="font-normal text-slate-500">{wps.find((w) => w.workplace_id === s.shift.workplace_id)?.name}</span></p>
          <p className="text-slate-500">{s.from_name} → {s.to_name || "anyone"}{s.note && ` · “${s.note}”`}</p></div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span>
          {s.status === "pending_colleague" && s.from_membership_id !== me && (!s.to_membership_id || s.to_membership_id === me) && <Button size="sm" onClick={() => act(`/swaps/${s.swap_id}/accept`, {}, "Swap accepted — waiting for manager")} className="rounded-full bg-[#00A85C]" data-testid={`swap-accept-${s.swap_id}`}>Take shift</Button>}
          {s.status.startsWith("pending") && s.from_membership_id === me && <Button size="sm" variant="outline" onClick={() => act(`/swaps/${s.swap_id}/cancel`, {}, "Swap cancelled")} className="rounded-full" data-testid={`swap-cancel-${s.swap_id}`}>Cancel</Button>}
          {mgr && s.status === "pending_manager" && <Button size="icon" onClick={() => act(`/swaps/${s.swap_id}/decision`, { decision: "approved" }, "Swap approved — roster updated")} className="h-8 w-8 rounded-full bg-[#00A85C]" aria-label="Approve" data-testid={`swap-approve-${s.swap_id}`}><Check className="h-4 w-4" /></Button>}
          {mgr && s.status.startsWith("pending") && <Button size="icon" variant="outline" onClick={() => act(`/swaps/${s.swap_id}/decision`, { decision: "rejected" }, "Swap rejected")} className="h-8 w-8 rounded-full" aria-label="Reject" data-testid={`swap-reject-${s.swap_id}`}><X className="h-4 w-4" /></Button>}
        </div>
      </li>))}</ul>
  );
};
